import "@/App.css";
import { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Menu, 
  X, 
  TreePine, 
  Axe, 
  Layers, 
  Flower2, 
  FenceIcon,
  BrickWall,
  Clock,
  DollarSign,
  ThumbsUp,
  Shield,
  FileText,
  ChevronRight,
  Send,
  ArrowRight
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import GalleryPage from "./pages/GalleryPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Company data
const PHONE_NUMBERS = ["913-415-9619", "817-939-1605"];
const EMAIL = "gsorto373@gmail.com";
const COMPANY_NAME = "German Sorto Tree Service";

// Service data - SEO optimized with location keywords
const SERVICES = [
  {
    id: "tree-trimming",
    title: "Tree Trimming",
    description: "Professional tree trimming and pruning in Kansas City. Our certified crew maintains tree health, improves property appearance, and reduces storm damage risk for homes across Kansas and Missouri.",
    icon: TreePine,
    image: "https://customer-assets.emergentagent.com/job_sorto-tree-kc/artifacts/1sgjjaje_5744291385490736081.jpeg",
    imageAlt: "Tree trimming service in Kansas City - German Sorto crew pruning mature tree"
  },
  {
    id: "tree-removal",
    title: "Tree Removal",
    description: "Safe tree removal service throughout the Kansas City metro. We handle dead, diseased, and hazardous trees with proper equipment and cleanup, plus optional stump grinding for a clean finish.",
    icon: Axe,
    image: "https://customer-assets.emergentagent.com/job_sorto-tree-kc/artifacts/6izksjfr_2937469907864273101.jpeg",
    imageAlt: "Tree removal service Kansas City - large tree being safely removed"
  },
  {
    id: "concrete",
    title: "Concrete Services",
    description: "Custom concrete work in Kansas City including stamped patios, driveways, walkways, and commercial sidewalks. Durable installations that enhance curb appeal and property value.",
    icon: Layers,
    image: "https://customer-assets.emergentagent.com/job_sorto-tree-kc/artifacts/bb96392x_2666299620353767973.jpeg",
    imageAlt: "Stamped concrete patio installation Kansas City by German Sorto"
  },
  {
    id: "landscaping",
    title: "Landscaping",
    description: "Full-service landscaping across Kansas and Missouri. From mulch beds and flower gardens to complete landscape design, we transform yards throughout the Kansas City area.",
    icon: Flower2,
    image: "https://customer-assets.emergentagent.com/job_sorto-tree-kc/artifacts/8n0d2w1i_7712207353650773321.jpeg",
    imageAlt: "Professional landscaping Kansas City - mulch bed and plant installation"
  },
  {
    id: "fence",
    title: "Fence Installation",
    description: "Custom fence installation in Kansas City for privacy, security, and property value. Wood, vinyl, and chain-link options built to last through Kansas and Missouri weather.",
    icon: FenceIcon,
    image: "https://customer-assets.emergentagent.com/job_sorto-tree-kc/artifacts/a90gotw8_2466655734892600536.jpeg",
    imageAlt: "Wood privacy fence installation Overland Park Kansas"
  },
  {
    id: "retaining-walls",
    title: "Retaining Walls",
    description: "Durable retaining wall construction for Kansas City properties. We prevent erosion, manage slopes, and create beautiful hardscape features that add lasting value to your landscape.",
    icon: BrickWall,
    image: "https://images.unsplash.com/photo-1761637823941-0ffae96ec487?w=800",
    imageAlt: "Stone retaining wall construction Kansas City area"
  }
];

// Why Choose Us data
const WHY_CHOOSE_US = [
  { icon: Shield, title: "Experienced & Reliable", description: "Years of professional experience serving Kansas City" },
  { icon: Clock, title: "Fast Response Times", description: "Quick quotes and efficient project completion" },
  { icon: DollarSign, title: "Affordable Pricing", description: "Competitive rates without compromising quality" },
  { icon: ThumbsUp, title: "Customer Satisfaction", description: "Dedicated to exceeding your expectations" },
  { icon: FileText, title: "Free Estimates", description: "No-obligation quotes for all services" }
];

// Gallery preview images (just 4 for homepage) - with SEO-optimized alt text
const GALLERY_PREVIEW = [
  { src: "https://customer-assets.emergentagent.com/job_sorto-tree-kc/artifacts/6izksjfr_2937469907864273101.jpeg", category: "Tree Work", alt: "Professional tree removal service in Kansas City" },
  { src: "https://customer-assets.emergentagent.com/job_sorto-tree-kc/artifacts/bb96392x_2666299620353767973.jpeg", category: "Concrete", alt: "Custom stamped concrete patio installation Kansas City" },
  { src: "https://customer-assets.emergentagent.com/job_sorto-tree-kc/artifacts/a90gotw8_2466655734892600536.jpeg", category: "Fences", alt: "Wood privacy fence installation Overland Park" },
  { src: "https://customer-assets.emergentagent.com/job_sorto-tree-kc/artifacts/kcjf7xb0_2946841483716020746.jpeg", category: "Landscaping", alt: "Professional landscaping with mulch bed Kansas City" },
];

// Service Areas - Cities served with unique content
const SERVICE_AREAS = [
  {
    city: "Kansas City",
    state: "MO/KS",
    description: "Serving the entire Kansas City metro area with expert tree care and outdoor services. Whether you need tree removal in downtown or landscaping in the suburbs, our experienced crew delivers reliable results."
  },
  {
    city: "Overland Park",
    state: "KS",
    description: "Trusted tree service provider in Overland Park. From mature oak trimming to full property landscaping, we help homeowners maintain beautiful, safe outdoor spaces."
  },
  {
    city: "Olathe",
    state: "KS",
    description: "Professional tree and landscape services for Olathe residents. Our team handles storm damage cleanup, tree removal, and custom concrete work with care and precision."
  },
  {
    city: "Lenexa",
    state: "KS",
    description: "Reliable tree trimming, fence installation, and retaining wall construction in Lenexa. We take pride in delivering quality workmanship for every project."
  },
  {
    city: "Shawnee",
    state: "KS",
    description: "Full-service tree and outdoor company serving Shawnee. From routine pruning to complete landscape transformations, we bring years of experience to every job."
  },
  {
    city: "Lee's Summit",
    state: "MO",
    description: "Expert tree service and hardscaping in Lee's Summit. Trust our team for safe tree removal, stamped concrete patios, and beautiful outdoor living spaces."
  },
  {
    city: "Independence",
    state: "MO",
    description: "Independence homeowners choose us for professional tree care and landscaping. We handle everything from emergency tree removal to custom retaining walls."
  },
  {
    city: "Blue Springs",
    state: "MO",
    description: "Serving Blue Springs with premium tree, concrete, and fence services. Fast response times and fair pricing make us the go-to choice for local homeowners."
  }
];

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_sorto-tree-kc/artifacts/ww3ixl6i_Logo%20Tienda%20Ecolo%CC%81gica%20Minimalista%20Beige%20%281%29.png";

// Header Component
const Header = ({ isScrolled, mobileMenuOpen, setMobileMenuOpen }) => {
  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'header-scrolled py-3' : 'bg-transparent py-4'
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a href="#hero" className="flex items-center gap-3" data-testid="logo">
            <img 
              src={LOGO_URL} 
              alt="German Sorto Tree Service" 
              className="h-20 md:h-24 w-auto"
            />
            <div className="hidden sm:block">
              <h1 className="text-lg md:text-2xl font-bold text-white uppercase tracking-wide leading-tight">
                German Sorto
              </h1>
              <p className="text-xs md:text-sm text-accent uppercase tracking-widest">Tree Service</p>
            </div>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            <a href="/#services" className="text-white/80 hover:text-accent transition-colors font-medium">Services</a>
            <a href="/#why-us" className="text-white/80 hover:text-accent transition-colors font-medium">Why Us</a>
            <a href="/#service-areas" className="text-white/80 hover:text-accent transition-colors font-medium">Service Area</a>
            <Link to="/gallery" className="text-white/80 hover:text-accent transition-colors font-medium">Gallery</Link>
            <a href="/#contact" className="text-white/80 hover:text-accent transition-colors font-medium">Contact</a>
          </nav>

          {/* Phone Numbers & CTA */}
          <div className="flex items-center gap-4">
            <div className="hidden md:flex flex-col items-end">
              <a href={`tel:${PHONE_NUMBERS[0].replace(/-/g, '')}`} className="text-white phone-link font-semibold">
                {PHONE_NUMBERS[0]}
              </a>
              <a href={`tel:${PHONE_NUMBERS[1].replace(/-/g, '')}`} className="text-white/70 text-sm phone-link">
                {PHONE_NUMBERS[1]}
              </a>
            </div>
            <a href={`tel:${PHONE_NUMBERS[0].replace(/-/g, '')}`}>
              <Button 
                className="bg-accent text-black hover:bg-accent/90 font-bold uppercase tracking-wider px-4 md:px-6 py-2 md:py-3 btn-glow"
                data-testid="call-now-btn"
              >
                <Phone className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Call Now</span>
                <span className="sm:hidden">Call</span>
              </Button>
            </a>

            {/* Mobile Menu Toggle */}
            <button 
              className="lg:hidden text-white p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <nav className="lg:hidden mt-4 pb-4 mobile-menu-enter" data-testid="mobile-nav">
            <div className="flex flex-col gap-3 bg-card/95 backdrop-blur-sm rounded-sm p-4 border border-white/10">
              <a href="/#services" onClick={() => setMobileMenuOpen(false)} className="text-white/80 hover:text-accent transition-colors font-medium py-2">Services</a>
              <a href="/#why-us" onClick={() => setMobileMenuOpen(false)} className="text-white/80 hover:text-accent transition-colors font-medium py-2">Why Us</a>
              <a href="/#service-areas" onClick={() => setMobileMenuOpen(false)} className="text-white/80 hover:text-accent transition-colors font-medium py-2">Service Area</a>
              <Link to="/gallery" onClick={() => setMobileMenuOpen(false)} className="text-white/80 hover:text-accent transition-colors font-medium py-2">Gallery</Link>
              <a href="/#contact" onClick={() => setMobileMenuOpen(false)} className="text-white/80 hover:text-accent transition-colors font-medium py-2">Contact</a>
              <div className="pt-2 border-t border-white/10">
                <p className="text-white font-semibold">{PHONE_NUMBERS[0]}</p>
                <p className="text-white/70 text-sm">{PHONE_NUMBERS[1]}</p>
              </div>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section 
      id="hero" 
      className="relative min-h-screen flex items-center hero-bg"
      style={{ backgroundImage: `url('https://customer-assets.emergentagent.com/job_sorto-tree-kc/artifacts/6izksjfr_2937469907864273101.jpeg')` }}
      data-testid="hero-section"
    >
      {/* Overlay */}
      <div className="absolute inset-0 hero-overlay"></div>
      
      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-32 md:py-40">
        <div className="max-w-3xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-primary/30 backdrop-blur-sm border border-primary/50 rounded-sm px-4 py-2 mb-6 animate-fade-in">
            <MapPin className="w-4 h-4 text-accent" />
            <span className="text-white/90 text-sm font-medium">Serving Kansas & Missouri</span>
          </div>

          {/* Headline */}
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white uppercase tracking-tighter leading-none mb-6 animate-fade-in-up">
            Professional Tree & Outdoor Services{" "}
            <span className="text-accent">You Can Trust</span>
          </h1>

          {/* Subheadline */}
          <p className="text-lg md:text-xl text-white/80 mb-8 max-w-2xl animate-fade-in-up stagger-1">
            Serving Kansas City with Reliable Tree, Concrete & Landscaping Solutions. 
            Quality workmanship and customer satisfaction guaranteed.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up stagger-2">
            <a href="#contact">
              <Button 
                className="bg-accent text-black hover:bg-accent/90 font-bold uppercase tracking-wider px-8 py-6 text-lg btn-glow w-full sm:w-auto"
                data-testid="get-estimate-btn"
              >
                Get a Free Estimate
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
            <a href={`tel:${PHONE_NUMBERS[0].replace(/-/g, '')}`}>
              <Button 
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 font-bold uppercase tracking-wider px-8 py-6 text-lg w-full sm:w-auto"
                data-testid="hero-call-btn"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call Now
              </Button>
            </a>
          </div>

          {/* Phone Numbers */}
          <div className="mt-8 flex flex-col sm:flex-row gap-4 animate-fade-in-up stagger-3">
            <div className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-accent" />
              <a href={`tel:${PHONE_NUMBERS[0].replace(/-/g, '')}`} className="text-white font-semibold text-lg phone-link">
                {PHONE_NUMBERS[0]}
              </a>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-accent" />
              <a href={`tel:${PHONE_NUMBERS[1].replace(/-/g, '')}`} className="text-white font-semibold text-lg phone-link">
                {PHONE_NUMBERS[1]}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
          <div className="w-1 h-2 bg-accent rounded-full"></div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  return (
    <section id="services" className="py-20 md:py-32 bg-background relative noise-overlay" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="mb-16">
          <div className="accent-line mb-4"></div>
          <h2 className="text-4xl md:text-5xl font-bold text-white uppercase tracking-tight mb-4">
            Our Tree & Outdoor Services in Kansas City
          </h2>
          <p className="text-white/60 text-lg max-w-2xl">
            From expert tree care to complete outdoor transformations, we deliver quality workmanship on every project across Kansas and Missouri.
          </p>
        </div>

        {/* Services Grid - Bento Style */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((service, index) => (
            <div 
              key={service.id}
              className={`service-card bg-card border border-white/5 rounded-sm overflow-hidden group ${
                index === 0 || index === 3 ? 'lg:row-span-2' : ''
              }`}
              data-testid={`service-card-${service.id}`}
            >
              {/* Image */}
              <div className={`relative overflow-hidden ${index === 0 || index === 3 ? 'h-48 lg:h-64' : 'h-40'}`}>
                <img 
                  src={service.image} 
                  alt={service.imageAlt}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent"></div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="service-icon w-12 h-12 rounded-sm flex items-center justify-center mb-4">
                  <service.icon className="w-6 h-6 text-accent" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-white uppercase tracking-tight mb-3">
                  {service.title}
                </h3>
                <p className="text-white/60 leading-relaxed">
                  {service.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyChooseUsSection = () => {
  return (
    <section id="why-us" className="py-20 md:py-32 bg-card relative" data-testid="why-us-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left - Content */}
          <div>
            <div className="accent-line mb-4"></div>
            <h2 className="text-4xl md:text-5xl font-bold text-white uppercase tracking-tight mb-6">
              Why Kansas City Chooses German Sorto
            </h2>
            <p className="text-white/60 text-lg mb-10">
              We're committed to delivering exceptional tree service and outdoor solutions across the Kansas City metro area with integrity, 
              professionalism, and attention to detail on every job.
            </p>

            <div className="space-y-6">
              {WHY_CHOOSE_US.map((item, index) => (
                <div 
                  key={index} 
                  className="flex items-start gap-4"
                  data-testid={`why-us-item-${index}`}
                >
                  <div className="w-12 h-12 bg-primary/30 border border-primary/50 rounded-sm flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-1">{item.title}</h3>
                    <p className="text-white/60">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Image */}
          <div className="relative">
            <div className="aspect-square rounded-sm overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_sorto-tree-kc/artifacts/nkzkbe93_2440954151375269199.jpeg"
                alt="Quality landscaping project by German Sorto Tree Service in Kansas City"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Accent border */}
            <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-accent/30 rounded-sm -z-10"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Service Areas Section - SEO for local cities
const ServiceAreasSection = () => {
  return (
    <section id="service-areas" className="py-20 md:py-32 bg-background relative noise-overlay" data-testid="service-areas-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="mb-12 text-center">
          <div className="w-16 h-1 bg-accent mb-4 mx-auto"></div>
          <h2 className="text-4xl md:text-5xl font-bold text-white uppercase tracking-tight mb-4">
            Serving the Kansas City Metro
          </h2>
          <p className="text-white/60 text-lg max-w-3xl mx-auto">
            Professional tree service, landscaping, concrete, and fence installation across Kansas and Missouri. 
            Local, licensed, and dedicated to quality workmanship in every neighborhood we serve.
          </p>
        </div>

        {/* Service Areas Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {SERVICE_AREAS.map((area, index) => (
            <article 
              key={index}
              className="bg-card border border-white/5 rounded-sm p-6 hover:border-accent/50 transition-all group"
              data-testid={`service-area-${area.city.toLowerCase().replace(/[^a-z]/g, '-')}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="w-5 h-5 text-accent" />
                <h3 className="text-xl font-bold text-white uppercase tracking-tight">
                  {area.city}
                </h3>
                <span className="text-white/40 text-sm">{area.state}</span>
              </div>
              <p className="text-white/60 text-sm leading-relaxed">
                {area.description}
              </p>
            </article>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center bg-primary/20 border border-primary/30 rounded-sm p-8">
          <h3 className="text-2xl font-bold text-white mb-2">
            Don't See Your City? We Still Serve You!
          </h3>
          <p className="text-white/60 mb-4">
            We cover the entire Kansas City metropolitan area. Call us for a free estimate.
          </p>
          <a href={`tel:${PHONE_NUMBERS[0].replace(/-/g, '')}`}>
            <Button className="bg-accent text-black hover:bg-accent/90 font-bold uppercase tracking-wider">
              <Phone className="w-4 h-4 mr-2" />
              {PHONE_NUMBERS[0]}
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

// Gallery Section (Preview)
const GallerySection = () => {
  return (
    <section id="gallery" className="py-20 md:py-32 bg-background relative noise-overlay" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <div className="accent-line mb-4"></div>
            <h2 className="text-4xl md:text-5xl font-bold text-white uppercase tracking-tight mb-4">
              Our Work
            </h2>
            <p className="text-white/60 text-lg max-w-2xl">
              Browse our portfolio of completed projects across Kansas City.
            </p>
          </div>
          <Link to="/gallery">
            <Button 
              variant="outline" 
              className="border-accent text-accent hover:bg-accent hover:text-black font-bold uppercase tracking-wider"
              data-testid="view-all-gallery"
            >
              View All Projects
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>

        {/* Gallery Grid - Preview */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {GALLERY_PREVIEW.map((image, index) => (
            <Link 
              to="/gallery"
              key={index} 
              className="gallery-item group relative aspect-square rounded-sm overflow-hidden border border-white/5"
              data-testid={`gallery-image-${index}`}
            >
              <img 
                src={image.src} 
                alt={image.alt}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <span className="text-accent text-sm font-medium">{image.category}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    setIsSubmitting(true);
  };

  if (submitted) {
    return (
      <section id="contact" className="py-20 md:py-32 bg-card relative" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">Thank You!</h2>
          <p className="text-white/70 text-lg">We'll contact you soon.</p>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="py-20 md:py-32 bg-card relative" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left - Contact Info */}
          <div>
            <div className="accent-line mb-4"></div>
            <h2 className="text-4xl md:text-5xl font-bold text-white uppercase tracking-tight mb-6">
              Get In Touch
            </h2>
            <p className="text-white/60 text-lg mb-10">
              Ready to transform your outdoor space? Contact us today for a free estimate. 
              We're here to help with all your tree and landscaping needs.
            </p>

            {/* Contact Details */}
            <div className="space-y-6 mb-10">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary/30 border border-primary/50 rounded-sm flex items-center justify-center">
                  <Phone className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <p className="text-white/60 text-sm mb-1">Call Us</p>
                  <a href={`tel:${PHONE_NUMBERS[0].replace(/-/g, '')}`} className="text-white font-semibold text-lg phone-link block">
                    {PHONE_NUMBERS[0]}
                  </a>
                  <a href={`tel:${PHONE_NUMBERS[1].replace(/-/g, '')}`} className="text-white/70 phone-link">
                    {PHONE_NUMBERS[1]}
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary/30 border border-primary/50 rounded-sm flex items-center justify-center">
                  <Mail className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <p className="text-white/60 text-sm mb-1">Email Us</p>
                  <a href={`mailto:${EMAIL}`} className="text-white font-semibold phone-link">
                    {EMAIL}
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary/30 border border-primary/50 rounded-sm flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <p className="text-white/60 text-sm mb-1">Service Area</p>
                  <p className="text-white font-semibold">Kansas City Metro</p>
                  <p className="text-white/70">Kansas & Missouri</p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="bg-primary/20 border border-primary/30 rounded-sm p-6">
              <h3 className="text-xl font-bold text-white mb-2">Call Today for a Free Estimate!</h3>
              <p className="text-white/60 mb-4">No obligation. Fast response times.</p>
              <a href={`tel:${PHONE_NUMBERS[0].replace(/-/g, '')}`}>
                <Button className="bg-accent text-black hover:bg-accent/90 font-bold uppercase tracking-wider btn-glow">
                  <Phone className="w-4 h-4 mr-2" />
                  {PHONE_NUMBERS[0]}
                </Button>
              </a>
            </div>
          </div>

          {/* Right - Contact Form */}
          <div className="bg-background/50 border border-white/5 rounded-sm p-6 md:p-8">
            <h3 className="text-2xl font-bold text-white uppercase tracking-tight mb-6">
              Request a Quote
            </h3>
            <form 
              action="https://api.web3forms.com/submit" 
              method="POST"
              onSubmit={handleSubmit}
              className="contact-form space-y-5" 
              data-testid="contact-form"
            >
              <input type="hidden" name="access_key" value="9c7014bc-37d5-45fa-b7b5-4c3e060819db" />
              <input type="hidden" name="subject" value="New Inquiry from German Sorto Tree Service Website" />
              <input type="hidden" name="redirect" value="https://sorto-tree-kc.preview.emergentagent.com/?submitted=true" />
              <div>
                <Input
                  type="text"
                  name="name"
                  placeholder="Your Name *"
                  required
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12"
                  data-testid="input-name"
                />
              </div>
              <div>
                <Input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number *"
                  required
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12"
                  data-testid="input-phone"
                />
              </div>
              <div>
                <Input
                  type="email"
                  name="email"
                  placeholder="Email Address *"
                  required
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12"
                  data-testid="input-email"
                />
              </div>
              <div>
                <select 
                  name="service"
                  className="w-full bg-white/5 border border-white/10 text-white h-12 px-4 rounded-md"
                  data-testid="select-service"
                >
                  <option value="" className="bg-[#1a1d1a]">Select Service Needed</option>
                  <option value="Tree Trimming" className="bg-[#1a1d1a]">Tree Trimming</option>
                  <option value="Tree Removal" className="bg-[#1a1d1a]">Tree Removal</option>
                  <option value="Concrete Services" className="bg-[#1a1d1a]">Concrete Services</option>
                  <option value="Landscaping" className="bg-[#1a1d1a]">Landscaping</option>
                  <option value="Fence Installation" className="bg-[#1a1d1a]">Fence Installation</option>
                  <option value="Retaining Walls" className="bg-[#1a1d1a]">Retaining Walls</option>
                  <option value="Other" className="bg-[#1a1d1a]">Other</option>
                </select>
              </div>
              <div>
                <Textarea
                  name="message"
                  placeholder="Tell us about your project..."
                  rows={4}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 resize-none"
                  data-testid="input-message"
                />
              </div>
              <Button 
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-accent text-black hover:bg-accent/90 font-bold uppercase tracking-wider py-6 btn-glow disabled:opacity-50"
                data-testid="submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <div className="spinner mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="py-16 md:py-24 bg-[#050705] border-t border-white/5" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8 mb-16">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <img 
                src={LOGO_URL} 
                alt="German Sorto Tree Service" 
                className="h-16 w-auto"
              />
            </div>
            <p className="text-white/60 mb-6 max-w-md">
              Professional tree and outdoor services serving the Kansas City metro area. 
              Quality workmanship, fair pricing, and customer satisfaction guaranteed.
            </p>
            <div className="flex gap-4">
              <a href={`tel:${PHONE_NUMBERS[0].replace(/-/g, '')}`} className="text-white font-semibold phone-link">
                {PHONE_NUMBERS[0]}
              </a>
              <span className="text-white/30">|</span>
              <a href={`tel:${PHONE_NUMBERS[1].replace(/-/g, '')}`} className="text-white/70 phone-link">
                {PHONE_NUMBERS[1]}
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-bold text-white uppercase tracking-wide mb-6">Services</h4>
            <ul className="space-y-3">
              {SERVICES.map((service) => (
                <li key={service.id}>
                  <a href="#services" className="text-white/60 footer-link hover:text-accent">
                    {service.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-lg font-bold text-white uppercase tracking-wide mb-6">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                <div>
                  <a href={`tel:${PHONE_NUMBERS[0].replace(/-/g, '')}`} className="text-white/80 block phone-link">
                    {PHONE_NUMBERS[0]}
                  </a>
                  <a href={`tel:${PHONE_NUMBERS[1].replace(/-/g, '')}`} className="text-white/60 phone-link">
                    {PHONE_NUMBERS[1]}
                  </a>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-accent flex-shrink-0" />
                <a href={`mailto:${EMAIL}`} className="text-white/80 phone-link">{EMAIL}</a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                <div className="text-white/80">
                  Kansas City Metro<br />
                  <span className="text-white/60">Kansas & Missouri</span>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-sm">
            © {currentYear} {COMPANY_NAME}. All rights reserved.
          </p>
          <p className="text-white/40 text-sm">
            Serving Kansas & Missouri
          </p>
        </div>
      </div>
    </footer>
  );
};

// Homepage Component
const HomePage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="bg-background min-h-screen">
      <Header 
        isScrolled={isScrolled} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
      />
      <main>
        <HeroSection />
        <ServicesSection />
        <WhyChooseUsSection />
        <ServiceAreasSection />
        <GallerySection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
};

// Main App Component
function App() {
  return (
    <Router>
      <div className="App">
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/gallery" element={<GalleryPage />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
